<template>
  <div class="w-full relative [ lg:flex lg:gap-x-[50px] ]">
    <a
      v-if="image"
      :href="href"
      target="_blank"
      class="lg:flex"
      :class="{ 'w-full order-last justify-end': direction === 'rtl' }"
    >
      <div class="md:absolute md:bottom-10 md:left-10 md:text-white ]">
        <span class="md:text-lg font-bold md:max-w-[170px] line-clamp-2"> {{ title }} </span>
        <span class="md:text-base md:max-w-[290px] line-clamp-2"> {{ description }} </span>
      </div>
      <picture>
        <AppSource
          :srcset="`${image} 480w, ${image} 800w, ${image} 1200w`"
          :alt="title"
          class="aspect-510-610"
          :class="{ '[ w-full lg:w-130 h-full ] rounded-6xs': variant === 'large' }"
          width="520"
          height="600"
          format="avif"
          :lazy="false"
        />
        <AppSource
          :srcset="`${image} 480w, ${image} 800w, ${image} 1200w`"
          :alt="title"
          class="aspect-510-610"
          :class="{ '[ w-full lg:w-130 h-full ] rounded-6xs': variant === 'large' }"
          width="520"
          height="600"
          format="webp"
          :lazy="false"
        />
        <AppSource
          :srcset="`${image} 480w, ${image} 800w, ${image} 1200w`"
          :alt="title"
          class="aspect-510-610"
          :class="{ '[ w-full lg:w-130 h-full ] rounded-6xs': variant === 'large' }"
          width="520"
          height="600"
          format="jpeg"
          :lazy="false"
        />
        <AppImage
          :src="image"
          alt=""
          class="aspect-510-610"
          :class="{ '[ w-full lg:w-130 h-full ] rounded-6xs': variant === 'large' }"
          width="520"
          height="600"
          :lazy="false"
        />
      </picture>
    </a>
    <div
      class="lg:max-w-[360px] [ absolute bottom-7 left-0 lg:static ] [ px-5 lg:px-0 ] text-left"
      :class="{
        ' self-center justify-center': alignment === 'center',
        ' self-end justify-end lg:pb-22': alignment === 'end',
      }"
    >
      <slot name="pre-title" />
      <div class="highlight">
        <h1
          class="max-h-25 max-w-[300px] text-white font-extrabold overflow-hidden"
          :class="{
            'text-lg lg:text-2.6xl': textSize === 'base',
            'text-xl lg:text-2xl-1': textSize === 'lg',
            'lg:mt-5': !$slots['pre-title'],
          }"
        >
          <a href="href" target="_blank" class="OfferCard__title line-clamp-2">
            {{ title }}
          </a>
        </h1>
      </div>

      <p v-if="description" class="text-base text-white line-clamp-2 lg:text-secondary-700 lg:mt-3">
        {{ description }}
      </p>
      <AppButton
        v-if="cta"
        variant="text-only"
        class="hidden lg:flex font-bold capitalize lg:mt-6 text-lg items-center"
      >
        {{ cta }}
      </AppButton>

      <AppButton v-if="cta" variant="text-only" class="lg:hidden capitalize lg:mt-6 text-lg" dark>
        {{ cta }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps({
  id: {
    type: [Number, String],
    required: false,
    default: '',
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: false,
    default: '',
  },
  cta: {
    type: String,
    required: false,
    default: '',
  },
  image: {
    type: String,
    required: false,
    default: null,
  },
  href: {
    type: String,
    required: true,
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value: string) => ['large'].includes(value),
  },
  alignment: {
    type: String,
    default: 'start',
    validator: (value: string) => ['start', 'center', 'end'].includes(value),
  },
  textSize: {
    type: String,
    default: 'base',
    validator: (value: string) => ['lg', 'md', 'base'].includes(value),
  },
  direction: {
    type: String,
    default: 'ltr',
    validator: (value: string) => ['ltr', 'rtl'].includes(value),
  },
});
</script>
<style lang="postcss" scoped>
@screen lg {
  .highlight {
    @apply relative inline-flex;
  }
  .highlight::after {
    content: '';
    background: url('~/assets/icons/path.svg');
    position: absolute;
    bottom: -10px;
    right: -22px;
    height: 55px;
    width: 85px;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    z-index: 1;
  }
}
[dir='rtl'] .highlight::after {
  transform: scaleX(-1);
}

.OfferCard {
  &__title {
    @apply text-primary-A900;
  }
}
</style>
